import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { useStatus } from '../../hooks';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { DropdownItem } from '@shared/api/types';
import { ScraperFilters } from '@features/cx/shared/utils/filters';

interface FilterPanelProps {
  currentFilters?: ScraperFilters;
  onApplyFilters: (filters: Partial<ScraperFilters>) => void;
  onResetFilters: () => void;
}

export const ConsumerOnboardingFilterPanel: React.FC<FilterPanelProps> = ({
  currentFilters = {},
  onApplyFilters,
  onResetFilters,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  
  const { data: statusesResponse } = useStatus({
    remoteUtilityId: Number(remoteUtilityId),
  });
  
  // Extract the result arrays from the API responses
  const statuses = statusesResponse?.result || [];
  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    status: string;
  }>({
    status: currentFilters?.status || '',
  });

  // Convert numeric status to string for form
  const selectedStatusValue = tempFilters.status
    ? String(tempFilters.status) 
    : 'ALL_STATUS';

  // Initialize form with current filter values
  const form = useForm({
    defaultValues: {
      status: selectedStatusValue,
    }
  });

  // Update temp filters when current filters change (e.g., from URL)
  useEffect(() => {
    const newTempFilters = {
      status: currentFilters?.status || '',
    };
    
    setTempFilters(newTempFilters);
    
    // Update form values
    const newSelectedStatusValue = newTempFilters.status
      ? String(newTempFilters.status) 
      : 'ALL_STATUS';
    
    form.setValue('status', newSelectedStatusValue);
  }, [currentFilters, form]);

  // Update form when tempFilters change
  useEffect(() => {
    const newSelectedStatusValue = tempFilters.status.length > 0 
      ? String(tempFilters.status) 
      : 'ALL_STATUS';
    
    form.setValue('status', newSelectedStatusValue);
  }, [tempFilters, form]);

  const handleStatusChange = (value: string) => {
    setTempFilters(prev => ({
      ...prev,
      status: (value === 'ALL_STATUS') ? '' : value
    }));
  };

  const handleCategoryChange = (value: string) => {
    setTempFilters(prev => ({
      ...prev,
      category: (value === 'ALL_CATEGORIES') ? '' : value
    }));
  };

  const handleApplyFilters = () => {
    const filtersToApply: Partial<ScraperFilters> = {};

    if (tempFilters.status.length > 0) {
      filtersToApply.status = tempFilters.status;
    }


    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    setTempFilters({
      status: '',
    });
    onResetFilters();
  };

  const handleSubmit = (data: any) => {
    handleApplyFilters();
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Account Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Select 
                value={selectedStatusValue} 
                onValueChange={(value) => {
                  field.onChange(value);
                  handleStatusChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_STATUS">All Status</SelectItem>
                    {statuses.map((status: DropdownItem) => (
                      <SelectItem key={status.value} value={String(status.value)}>
                        {status.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>
                
        <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">
            Apply Filters
          </Button>
        </div>
      </div>
    </form>
  );
};